import React from 'react';
import logo from '@/assets/Logo.png';

interface SwatchProps {
  name: string;
  hex: string;
  on?: string;
  alpha?: number;
  className?: string;
}

function Swatch({ name, hex, on = '#0A0324', alpha, className = '' }: SwatchProps): React.ReactElement {
  const a = alpha == null ? 1 : alpha;
  return (
    <div
      className={`rounded-xl p-3 flex flex-col justify-between min-h-[88px] ${className}`}
      style={{ backgroundColor: hex, opacity: a }}
    >
      <span className="text-[12px] font-semibold" style={{ color: on }}>
        {name}
      </span>
      <span className="font-mono text-[10px] opacity-70" style={{ color: on }}>
        {hex.toUpperCase()}
        {a < 1 ? ` · ${Math.round(a * 100)}%` : ''}
      </span>
    </div>
  );
}

function SectionTitle({ eyebrow, title, lede }: { eyebrow: string; title: string; lede?: string }): React.ReactElement {
  return (
    <div className="mb-6">
      <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-2">
        {eyebrow}
      </p>
      <h2 className="font-display text-3xl font-bold text-white mb-2">{title}</h2>
      {lede && <p className="text-sm text-gray-400 max-w-2xl leading-relaxed">{lede}</p>}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }): React.ReactElement {
  return (
    <div
      className={`rounded-2xl bg-gray-900/80 border border-gray-800 p-6 ${className}`}
    >
      {children}
    </div>
  );
}

function Marketing(): React.ReactElement {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(circle at 20% 30%, rgba(235,43,188,0.45), transparent 50%), radial-gradient(circle at 80% 60%, rgba(6,182,212,0.35), transparent 50%), radial-gradient(circle at 50% 90%, rgba(168,67,251,0.35), transparent 55%)',
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 py-20">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-300 mb-4">
            SocialSpark · Brand Guidelines
          </p>
          <h1 className="font-display text-5xl md:text-6xl font-extrabold text-white mb-6 leading-[1.05] tracking-tight">
            One brand,
            <br />
            two product personalities.
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl leading-relaxed">
            SocialSpark connects two audiences in one ecosystem: the people going out, and the
            owners running the venues. Each gets its own visual language — sister design systems
            that share a single brand DNA, with the Spark gradient as the cross-brand signature.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-6 py-12 space-y-16">
        {/* 01 · Spark gradient */}
        <section>
          <SectionTitle
            eyebrow="01 · Signature"
            title="The Spark gradient"
            lede="The single most recognizable element across both product personalities. Magenta to cyan, ~135°. It appears on hero CTAs in consumer, on every focus state in business, and as the brand mark on every social asset."
          />
          <Card className="overflow-hidden">
            <div
              className="rounded-xl flex items-center justify-center text-white font-display text-3xl font-extrabold tracking-tight"
              style={{
                height: 240,
                background: 'linear-gradient(135deg, #EB2BBC 0%, #06B6D4 100%)',
              }}
            >
              #EB2BBC → #06B6D4
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="rounded-xl bg-gray-950 border border-gray-800 p-4">
                <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-1">
                  Direction
                </p>
                <p className="text-sm text-gray-300">
                  Always <span className="font-mono text-gray-100">135°</span> diagonal — magenta top-left, cyan bottom-right.
                </p>
              </div>
              <div className="rounded-xl bg-gray-950 border border-gray-800 p-4">
                <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-1">
                  Use for
                </p>
                <p className="text-sm text-gray-300">
                  Hero CTAs · FAB · focus states · brand mark accents · logo treatments.
                </p>
              </div>
              <div className="rounded-xl bg-gray-950 border border-gray-800 p-4">
                <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-1">
                  Never
                </p>
                <p className="text-sm text-gray-300">
                  As a full-bleed background · behind body copy · with the colors swapped or angle rotated.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* 02 · Logo */}
        <section>
          <SectionTitle
            eyebrow="02 · Logo"
            title="The flame-in-pin"
            lede="A rounded location pin holding a flame inside, rendered in the Spark gradient. The flame is the moment, the pin is the place — SocialSpark connects the two."
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-4">
                Primary
              </p>
              <div className="bg-gray-950 rounded-xl p-12 flex items-center justify-center">
                <img src={logo} alt="SocialSpark" className="w-24 h-24" />
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Full color on dark backgrounds. Pin radius matches the icon's interior flame curve.
              </p>
            </Card>
            <Card>
              <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-4">
                Clear space
              </p>
              <div className="bg-gray-950 rounded-xl p-8 flex items-center justify-center relative">
                <div className="absolute inset-4 border border-dashed border-violet-500/40 rounded-lg pointer-events-none" />
                <img src={logo} alt="SocialSpark" className="w-20 h-20" />
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Always keep clear space around the logo equal to ¼ of the mark's height. Never crop it tighter.
              </p>
            </Card>
            <Card>
              <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-4">
                Lockup
              </p>
              <div className="bg-gray-950 rounded-xl p-8 flex items-center justify-center gap-2">
                <img src={logo} alt="" className="w-10 h-10" />
                <span className="font-display text-2xl font-bold">
                  <span className="text-white">Social</span>
                  <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                    Spark
                  </span>
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Horizontal lockup pairs the mark with the wordmark. "Spark" always uses the Spark gradient on the text.
              </p>
            </Card>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <p className="text-[11px] font-semibold tracking-wider uppercase text-emerald-400 mb-3">
                Do
              </p>
              <ul className="text-sm text-gray-300 space-y-2 leading-relaxed">
                <li>· Use the full-color logo on dark backgrounds (the natural home)</li>
                <li>· Maintain ¼-height clear space on every side</li>
                <li>· Pair the lockup wordmark only in display + bold weights</li>
                <li>· Use the white silhouette version on photos with busy color</li>
              </ul>
            </Card>
            <Card>
              <p className="text-[11px] font-semibold tracking-wider uppercase text-rose-400 mb-3">
                Don't
              </p>
              <ul className="text-sm text-gray-300 space-y-2 leading-relaxed">
                <li>· Swap the gradient stops or rotate the angle</li>
                <li>· Outline the logo or add drop shadows</li>
                <li>· Skew, stretch, or rotate the mark</li>
                <li>· Place the gradient logo on a low-contrast background</li>
              </ul>
            </Card>
          </div>
        </section>

        {/* 03 · Two systems side by side */}
        <section>
          <SectionTitle
            eyebrow="03 · Color systems"
            title="Two personalities, one DNA"
            lede="Each product gets its own palette tuned for its audience. Consumer feels social and energetic; business feels professional and data-rich. Both stay in dark mode because both audiences use the app at night."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Consumer */}
            <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/30 to-purple-950/20">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[11px] font-semibold tracking-wider uppercase text-fuchsia-300 mb-1">
                    Consumer
                  </p>
                  <h3 className="font-display text-xl font-bold text-white">For going out</h3>
                </div>
                <span className="text-[10px] font-mono text-fuchsia-300 bg-fuchsia-900/40 border border-fuchsia-700/40 rounded-full px-2 py-1">
                  Magenta · pill · glass
                </span>
              </div>

              <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-2">
                Brand
              </p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <Swatch name="Primary" hex="#EB2BBC" on="#FFF4FB" />
                <Swatch name="Secondary" hex="#A843FB" on="#FFFFFF" />
                <Swatch name="Tertiary" hex="#339FFC" on="#001E3D" />
                <Swatch name="Accent" hex="#FBBF24" on="#422006" />
              </div>

              <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-2">
                Surface
              </p>
              <div className="grid grid-cols-5 gap-2 mb-4">
                <Swatch name="Bg" hex="#0A0324" on="#F2E7F1" />
                <Swatch name="Low" hex="#150A2F" on="#F2E7F1" />
                <Swatch name="Surf" hex="#1F1339" on="#F2E7F1" />
                <Swatch name="High" hex="#291D43" on="#F2E7F1" />
                <Swatch name="+1" hex="#33274D" on="#F2E7F1" />
              </div>

              <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-2">
                Semantic
              </p>
              <div className="grid grid-cols-4 gap-2">
                <Swatch name="Success" hex="#1F8A48" on="#FFFFFF" />
                <Swatch name="Warning" hex="#F59E0B" on="#422006" />
                <Swatch name="Error" hex="#FF6B6B" on="#5C0008" />
                <Swatch name="Info" hex="#38BDF8" on="#082F49" />
              </div>
            </Card>

            {/* Business */}
            <Card className="border-blue-500/30 bg-gradient-to-br from-blue-950/30 to-slate-950/20">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[11px] font-semibold tracking-wider uppercase text-blue-300 mb-1">
                    Business
                  </p>
                  <h3 className="font-display text-xl font-bold text-white">For running the venue</h3>
                </div>
                <span className="text-[10px] font-mono text-amber-300 bg-amber-900/30 border border-amber-700/40 rounded-full px-2 py-1">
                  Blue · 8px · gold for $
                </span>
              </div>

              <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-2">
                Brand
              </p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <Swatch name="Primary" hex="#339FFC" on="#001E3D" />
                <Swatch name="Secondary" hex="#64748B" on="#FFFFFF" />
                <Swatch name="Tertiary" hex="#FBBF24" on="#422006" />
                <Swatch name="Accent" hex="#FBBF24" on="#422006" />
              </div>

              <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-2">
                Surface
              </p>
              <div className="grid grid-cols-5 gap-2 mb-4">
                <Swatch name="Bg" hex="#0A1424" on="#E8EEF7" />
                <Swatch name="Low" hex="#0C1B2F" on="#E8EEF7" />
                <Swatch name="Surf" hex="#122340" on="#E8EEF7" />
                <Swatch name="High" hex="#182B4C" on="#E8EEF7" />
                <Swatch name="+1" hex="#1E3559" on="#E8EEF7" />
              </div>

              <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-2">
                Semantic
              </p>
              <div className="grid grid-cols-4 gap-2">
                <Swatch name="Success" hex="#4ADE80" on="#052E16" />
                <Swatch name="Warning" hex="#F59E0B" on="#422006" />
                <Swatch name="Error" hex="#FF6B6B" on="#5C0008" />
                <Swatch name="Info" hex="#38BDF8" on="#082F49" />
              </div>
            </Card>
          </div>

          <Card className="mt-6">
            <p className="text-[11px] font-semibold tracking-wider uppercase text-violet-400 mb-3">
              How they differ
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div>
                <p className="text-white font-semibold mb-1">Shape language</p>
                <p className="text-gray-400 leading-relaxed">
                  Consumer = pill (radius 9999) on all buttons. Business = 8px rounded rect. Same M3
                  bones, different posture.
                </p>
              </div>
              <div>
                <p className="text-white font-semibold mb-1">Surface tint</p>
                <p className="text-gray-400 leading-relaxed">
                  Consumer canvas is purple-warm (#0A0324). Business is navy-cool (#0A1424). Both
                  dark — bars and dashboards both at night.
                </p>
              </div>
              <div>
                <p className="text-white font-semibold mb-1">Money color</p>
                <p className="text-gray-400 leading-relaxed">
                  Business reserves gold (#FBBF24) for revenue / payouts / charges. Eye scans
                  straight to it. Consumer uses gold only for VIP / milestone moments.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* 04 · Typography */}
        <section>
          <SectionTitle
            eyebrow="04 · Typography"
            title="Geist, scaled two ways"
            lede="Both systems use Geist — modern, geometric, neutral. Consumer uses the full M3 scale; business compresses it ~15% for data density. ui-monospace handles numerics in tables and KPIs."
          />
          <Card>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-[11px] font-semibold tracking-wider uppercase text-fuchsia-300 mb-4">
                  Consumer scale
                </p>
                <div className="space-y-3">
                  <div>
                    <p className="font-display text-[57px] font-bold text-white leading-none tracking-tight">
                      Tonight
                    </p>
                    <p className="font-mono text-[10px] text-gray-500 mt-1">Display L · 57 / 64</p>
                  </div>
                  <div>
                    <p className="font-display text-[32px] font-bold text-white leading-tight">
                      The Howl · Tonight
                    </p>
                    <p className="font-mono text-[10px] text-gray-500 mt-1">Headline L · 32 / 40</p>
                  </div>
                  <div>
                    <p className="text-[16px] font-medium text-white leading-snug">
                      Drinks lean low-key but the patio fills up after 10:30.
                    </p>
                    <p className="font-mono text-[10px] text-gray-500 mt-1">Body L · 16 / 24</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold tracking-wider uppercase text-blue-300 mb-4">
                  Business scale
                </p>
                <div className="space-y-3">
                  <div>
                    <p className="font-display text-[48px] font-bold text-white leading-none tracking-tight">
                      $4,280
                    </p>
                    <p className="font-mono text-[10px] text-gray-500 mt-1">Display L · 48 / 56</p>
                  </div>
                  <div>
                    <p className="font-display text-[28px] font-bold text-white leading-tight">
                      142 live · +24 in last hour
                    </p>
                    <p className="font-mono text-[10px] text-gray-500 mt-1">Headline L · 28 / 36</p>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-white leading-snug">
                      Bottle service trending up 18% week-over-week.
                    </p>
                    <p className="font-mono text-[10px] text-gray-500 mt-1">Body L · 14 / 20</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-800 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="font-mono text-violet-400 font-semibold mb-1">Display + Headline</p>
                <p className="text-gray-400 leading-relaxed">
                  Geist Bold · tight letter-spacing (-2%). For titles, big numbers, hero moments.
                </p>
              </div>
              <div>
                <p className="font-mono text-violet-400 font-semibold mb-1">Title + Body</p>
                <p className="text-gray-400 leading-relaxed">
                  Geist Medium / Regular. Default text weight. Line-height 1.4–1.5 for readability.
                </p>
              </div>
              <div>
                <p className="font-mono text-violet-400 font-semibold mb-1">Numerics</p>
                <p className="text-gray-400 leading-relaxed">
                  ui-monospace · tabular figures. Used in KPIs, table numeric columns, durations,
                  timestamps.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* 05 · Voice */}
        <section>
          <SectionTitle
            eyebrow="05 · Voice & tone"
            title="Two audiences, two voices"
            lede="The way SocialSpark talks to a guest pre-going-out is different from how it talks to an owner mid-shift. Both stay short, specific, and human."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-fuchsia-500/30">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-fuchsia-300 mb-3">
                Consumer voice
              </p>
              <h3 className="font-display text-xl font-bold text-white mb-2">Friend-recommended</h3>
              <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                Confident, specific, observational. Like a friend who's been there and knows the
                place. Never marketing-speak.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span className="text-gray-300">"The patio fills up after 10:30."</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  <span className="text-gray-500">"Experience the vibrant nightlife scene!"</span>
                </div>
              </div>
            </Card>
            <Card className="border-blue-500/30">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-blue-300 mb-3">
                Business voice
              </p>
              <h3 className="font-display text-xl font-bold text-white mb-2">Operator-direct</h3>
              <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                Specific, numeric, actionable. Owners are reading at 11pm before service — say what
                changed and what to do about it.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span className="text-gray-300">"Capacity at 92% — consider surge."</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  <span className="text-gray-500">"Things are heating up in your venue!"</span>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* 06 · Quick reference */}
        <section>
          <SectionTitle
            eyebrow="06 · Quick reference"
            title="The 30-second version"
            lede="If you're designing or writing for SocialSpark and only have 30 seconds, here's the load-bearing rules."
          />
          <Card>
            <ul className="space-y-3 text-sm text-gray-300 leading-relaxed">
              <li>
                <span className="text-violet-300 font-semibold">Spark gradient</span> (#EB2BBC →
                #06B6D4 at 135°) is the brand mark. Use it as the focus state on every interactive
                element in both systems.
              </li>
              <li>
                <span className="text-fuchsia-300 font-semibold">Consumer</span> = magenta primary,
                pill buttons, purple-tinted dark, Liquid Glass for chrome. Playful but specific.
              </li>
              <li>
                <span className="text-blue-300 font-semibold">Business</span> = blue primary, 8px
                rounded rect buttons, navy-tinted dark, gold for revenue. Snappy and operator-direct.
              </li>
              <li>
                <span className="text-amber-300 font-semibold">Gold (#FBBF24)</span> is for money.
                Revenue, payouts, charges, min spend. Never for decoration.
              </li>
              <li>
                <span className="text-gray-100 font-semibold">Geist</span> for everything. Bold for
                displays, Medium for titles, Regular for body. ui-monospace for numerics.
              </li>
              <li>
                <span className="text-gray-100 font-semibold">Dark mode always.</span> Light mode is
                not part of either system.
              </li>
            </ul>
          </Card>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="/designs"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-gray-950 hover:opacity-90 transition-all"
            >
              Open design systems →
            </a>
            <a
              href="https://www.figma.com/design/CBJ5J4ZzkY596o3bL485G0/Consumer-Design-System"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700 transition-all"
            >
              Consumer Figma ↗
            </a>
            <a
              href="https://www.figma.com/design/evhRrFw1jPmA8EhmJmr585/Business-Design-System"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700 transition-all"
            >
              Business Figma ↗
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Marketing;
